import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, Notification } from '../services/notificationService';
import styles from '../styles/modules/MaintenanceModal.module.css';

interface MaintenanceModalProps {
  onNotificationRead?: () => void;
}

const MaintenanceModal: React.FC<MaintenanceModalProps> = ({ onNotificationRead }) => {
  const { t } = useTranslation();
  const [maintenances, setMaintenances] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchMaintenances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications();
      
      // Filter for UNREAD maintenance only
      const maintenanceList = response.notifications.filter(
        (notif: Notification) => notif.category === 'maintenance' && !notif.isRead
      );
      
      setMaintenances(maintenanceList);
      if (maintenanceList.length > 0) {
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching maintenance notices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaintenances();
  }, [fetchMaintenances]);

  const handleNext = () => {
    if (currentIndex < maintenances.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleMarkAsRead = async () => {
    const currentMaintenance = maintenances[currentIndex];
    if (!currentMaintenance || markingRead) return;

    setMarkingRead(true);
    try {
      await notificationService.markAsRead(currentMaintenance._id);
      const updated = maintenances.filter((a) => a._id !== currentMaintenance._id);
      setMaintenances(updated);

      if (updated.length === 0) {
        setShowModal(false);
        setCurrentIndex(0);
      } else if (currentIndex >= updated.length) {
        setCurrentIndex(updated.length - 1);
      }
      
      // Wait a bit before notifying parent to allow backend to commit
      if (onNotificationRead) {
        setTimeout(() => {
          onNotificationRead();
        }, 300);
      }
    } catch (error) {
      console.error('Error marking maintenance as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  if (!showModal || maintenances.length === 0 || loading) {
    return null;
  }

  const currentMaintenance = maintenances[currentIndex];

  return (
    <div className={styles.maintenanceOverlay} onClick={() => setShowModal(false)}>
      <div className={styles.maintenanceModal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          className={styles.maintenanceClose}
          onClick={() => setShowModal(false)}
          title={t('common.close')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Content */}
        <div className={styles.maintenanceContent}>
          <div className={styles.maintenanceDate}>
            {new Date(currentMaintenance.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
          <div className={styles.maintenanceIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h2 className={styles.maintenanceTitle}>{currentMaintenance.title}</h2>
          <div className={styles.maintenanceBody}>{currentMaintenance.content}</div>
        </div>

        {/* Navigation */}
        {maintenances.length > 1 && (
          <>
            <div className={styles.maintenanceCounter}>
              {currentIndex + 1} of {maintenances.length}
            </div>
            <div className={styles.maintenanceNavigation}>
              <button
                className={styles.btnNav}
                onClick={handlePrev}
                disabled={currentIndex === 0}
                title={t('notifications.previousAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className={styles.btnNav}
                onClick={handleNext}
                disabled={currentIndex === maintenances.length - 1}
                title={t('notifications.nextAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </>
        )}

        <div className={styles.maintenanceActions}>
          <button
            className={styles.btnMaintenanceAcknowledge}
            onClick={handleMarkAsRead}
            disabled={markingRead}
            title={t('notifications.markAsRead')}
            aria-label={t('notifications.markAsRead')}
          >
            {markingRead ? (
              t('common.loading')
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceModal;
